import styled, { keyframes } from 'styled-components';
import COLORS from '../../constants/colors';

/* Pulse blends directly into the admin page — no outer card, no dark
   sidebar. A slim utility row (history + new chat) sits above the chat,
   messages flow on the page background, and the composer is a floating
   rounded search bar. */
export const PulseRoot = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  color: ${COLORS.textDark};

  /* Break out of the admin ContentScrollArea's right padding so the message
     scrollbar sits flush against the far edge of the page rather than floating
     24px inside it. Inner content stays padded via UtilityBar/MessageContainer. */
  margin-right: -24px;

  @media (max-width: 1024px) {
    margin-right: -16px;
  }
`;

export const UtilityBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
  padding: 4px 24px 0 0;

  @media (max-width: 768px) {
    /* Sit higher and centered on mobile, and drop the right-edge offset
       so the row is symmetric on screen. */
    justify-content: center;
    padding: 0 12px 6px;
    margin-top: -4px;
  }
`;

export const UtilityButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: ${COLORS.$white};
  border: 1px solid ${COLORS.border};
  border-radius: 18px;
  font-size: 12.5px;
  font-weight: 500;
  color: ${COLORS.textMedium};
  cursor: pointer;
  transition: all 0.12s;

  &:hover {
    border-color: ${COLORS.$mainTiffany};
    color: ${COLORS.textDark};
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const HistoryPanel = styled.div`
  background: ${COLORS.$white};
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  width: 320px;
  max-height: 420px;
  overflow-y: auto;
  padding: 6px 0 10px;

  .group-label {
    padding: 10px 16px 4px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${COLORS.textMuted};
    font-weight: 700;
  }

  .empty {
    padding: 16px;
    font-size: 12.5px;
    color: ${COLORS.textMuted};
  }
`;

export const HistoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;
  color: ${COLORS.textDark};
  background: ${(p) => (p.$active ? 'rgba(78, 205, 196, 0.12)' : 'transparent')};
  border-left: 3px solid ${(p) => (p.$active ? COLORS.$mainTiffany : 'transparent')};
  transition: background 0.12s;

  &:hover { background: rgba(0, 0, 0, 0.04); }

  .title {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .actions { display: none; gap: 2px; }
  &:hover .actions { display: flex; }

  .icon-btn {
    background: transparent;
    border: none;
    color: ${COLORS.textMuted};
    cursor: pointer;
    padding: 2px 5px;
    border-radius: 4px;

    &:hover { color: ${COLORS.$deepTiffany}; background: rgba(0, 0, 0, 0.05); }
  }
`;

export const PromptMenu = styled.div`
  background: ${COLORS.$white};
  border: 1px solid ${COLORS.border};
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
  width: 340px;
  max-height: 420px;
  overflow-y: auto;
  padding: 6px;

  .menu-label {
    padding: 8px 10px 6px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    color: ${COLORS.textMuted};
    font-weight: 700;
  }

  .prompt {
    display: block;
    width: 100%;
    text-align: left;
    background: transparent;
    border: none;
    border-radius: 8px;
    padding: 9px 10px;
    font-size: 13px;
    line-height: 1.4;
    color: ${COLORS.textDark};
    cursor: pointer;
    transition: background 0.12s;

    &:hover { background: rgba(78, 205, 196, 0.12); }
  }
`;

export const MessageScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 24px 0;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }
`;

export const MessageContainer = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 768px) {
    padding: 0 14px;
  }
`;

export const Bubble = styled.div`
  margin: 18px 0;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-direction: ${(p) => (p.$role === 'user' ? 'row-reverse' : 'row')};

  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 12px;
    color: ${COLORS.$white};
    background: ${(p) => (p.$role === 'user' ? '#4a4f55' : COLORS.$mainBlack)};
  }

  /* Assistant replies sit directly on the page; user messages get a soft
     tinted bubble — keeps the chat feeling native to the page. */
  .body {
    max-width: ${(p) => (p.$role === 'user' ? '75%' : '100%')};
    flex: ${(p) => (p.$role === 'user' ? '0 1 auto' : '1')};
    background: ${(p) => (p.$role === 'user' ? 'rgba(78, 205, 196, 0.12)' : COLORS.$white)};
    border: 1px solid ${(p) => (p.$role === 'user' ? 'rgba(78, 205, 196, 0.35)' : COLORS.border)};
    border-radius: ${(p) => (p.$role === 'user' ? '16px 16px 4px 16px' : '4px 16px 16px 16px')};
    padding: 12px 16px;
    font-size: 14px;
    line-height: 1.55;
    color: ${COLORS.textDark};
    word-wrap: break-word;
  }

  .md-root > :first-child { margin-top: 0; }
  .md-root > :last-child { margin-bottom: 0; }
  .md-h { margin: 14px 0 6px; font-weight: 600; }
  .md-h1 { font-size: 17px; }
  .md-h2 { font-size: 15px; }
  .md-h3 { font-size: 14px; color: ${COLORS.textMedium}; }
  .md-p { margin: 8px 0; }
  .md-ul, .md-ol { margin: 8px 0 8px 22px; padding: 0; }
  .md-ul li, .md-ol li { margin: 3px 0; }
  .md-code {
    background: ${COLORS.backgroundLight};
    padding: 1px 5px;
    border-radius: 4px;
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 12px;
  }
  .md-table {
    width: 100%;
    border-collapse: collapse;
    margin: 12px 0;
    font-size: 13px;
    border: 1px solid ${COLORS.border};
    border-radius: 8px;
    overflow: hidden;

    th, td {
      padding: 7px 12px;
      text-align: left;
      border-bottom: 1px solid ${COLORS.border};
      vertical-align: top;
    }
    th {
      background: ${COLORS.backgroundLight};
      font-weight: 600;
      color: ${COLORS.textMedium};
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.4px;
      white-space: nowrap;
    }
    td:last-child, th:last-child { text-align: right; }
    tr:last-child td { border-bottom: none; }
    tbody tr:nth-child(even) { background: rgba(0, 0, 0, 0.02); }
  }
`;

export const ToolCallList = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const ToolCallCard = styled.details`
  background: ${COLORS.backgroundLight};
  border: 1px solid ${COLORS.border};
  border-radius: 10px;
  padding: 6px 10px;
  font-size: 12px;

  summary {
    cursor: pointer;
    font-weight: 600;
    color: ${COLORS.$deepTiffany};
    user-select: none;

    &::marker { color: ${COLORS.$mainTiffany}; }
  }

  .card-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 6px;
  }

  .csv-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: ${COLORS.$white};
    border: 1px solid ${COLORS.border};
    border-radius: 14px;
    padding: 3px 10px;
    font-size: 11.5px;
    font-weight: 600;
    color: ${COLORS.textMedium};
    cursor: pointer;
    transition: all 0.12s;

    &:hover {
      border-color: ${COLORS.$mainTiffany};
      color: ${COLORS.$deepTiffany};
    }
  }

  .data-body { margin-top: 8px; }

  pre {
    margin: 8px 0 0;
    padding: 8px;
    background: #2a2d31;
    color: #e6e6e6;
    border-radius: 8px;
    overflow-x: auto;
    font-family: 'SFMono-Regular', Consolas, monospace;
    font-size: 11px;
    line-height: 1.45;
    max-height: 220px;
  }
`;

/* Compact table used to show tool results (breakdowns, series, key figures)
   in a scannable, expandable way. */
export const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 6px 0;
  background: ${COLORS.$white};
  border: 1px solid ${COLORS.border};
  border-radius: 8px;
  overflow: hidden;
  font-size: 12px;

  caption {
    caption-side: top;
    text-align: left;
    padding: 4px 2px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${COLORS.textMedium};
  }

  th, td {
    padding: 6px 10px;
    text-align: left;
    border-bottom: 1px solid ${COLORS.border};
  }
  th {
    background: ${COLORS.backgroundLight};
    font-weight: 600;
    color: ${COLORS.textMedium};
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }
  tr:last-child td { border-bottom: none; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; font-weight: 600; }

  .more-note {
    color: ${COLORS.textMuted};
    font-style: italic;
  }
`;

const blink = keyframes`
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
`;

export const Typing = styled.div`
  display: inline-flex;
  gap: 4px;
  align-items: center;
  color: ${COLORS.textMuted};
  font-size: 13px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${COLORS.$mainTiffany};
    animation: ${blink} 1.2s infinite;
  }
  span:nth-child(2) { animation-delay: 0.15s; }
  span:nth-child(3) { animation-delay: 0.3s; }
`;

export const Composer = styled.div`
  flex-shrink: 0;
  padding: 10px 24px 18px;

  @media (max-width: 768px) {
    padding: 8px 12px 12px;
  }
`;

export const ComposerInner = styled.div`
  max-width: 860px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 8px;

  /* Floating search-bar style input */
  .row {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    background: ${COLORS.$white};
    border: 1px solid ${COLORS.border};
    border-radius: 24px;
    padding: 6px 6px 6px 18px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus-within {
      border-color: ${COLORS.$mainTiffany};
      box-shadow: 0 2px 16px rgba(78, 205, 196, 0.25);
    }
  }

  textarea {
    flex: 1;
    resize: none;
    min-height: 36px;
    max-height: 180px;
    padding: 8px 0;
    border: none;
    font-size: 14px;
    font-family: inherit;
    line-height: 1.45;
    color: ${COLORS.textDark};
    background: transparent;

    &:focus { outline: none; }
  }

  button.send {
    height: 38px;
    width: 38px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${COLORS.$mainTiffany};
    color: ${COLORS.$mainBlack};
    border: none;
    border-radius: 50%;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s;

    &:hover { background: ${COLORS.$brightTiffany}; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
`;

export const Suggestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;

  .pill {
    background: ${COLORS.$white};
    border: 1px solid ${COLORS.border};
    border-radius: 20px;
    padding: 6px 12px;
    font-size: 12px;
    color: ${COLORS.textMedium};
    cursor: pointer;
    transition: all 0.12s;

    &:hover {
      background: ${COLORS.$mainTiffany};
      color: ${COLORS.$mainBlack};
      border-color: ${COLORS.$mainTiffany};
    }
  }
`;

export const EmptyState = styled.div`
  flex: 1;
  min-height: 55vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  color: ${COLORS.textLight};

  .heart {
    font-size: 48px;
    color: ${COLORS.$mainTiffany};
    margin-bottom: 12px;
  }

  h2 {
    margin: 0 0 8px;
    font-size: 22px;
    color: ${COLORS.textDark};
    font-weight: 600;
  }

  p {
    margin: 0 0 20px;
    max-width: 440px;
    font-size: 14px;
    line-height: 1.55;
  }

  .examples {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
    max-width: 720px;
    width: 100%;

    .example {
      text-align: left;
      background: ${COLORS.$white};
      border: 1px solid ${COLORS.border};
      border-radius: 12px;
      padding: 12px 14px;
      cursor: pointer;
      font-size: 13px;
      color: ${COLORS.textDark};
      transition: all 0.12s;

      &:hover {
        border-color: ${COLORS.$mainTiffany};
        box-shadow: 0 2px 6px ${COLORS.cardShadow};
        transform: translateY(-1px);
      }
    }
  }

  @media (max-width: 768px) {
    padding: 24px 14px;

    .heart { font-size: 38px; }
    h2 { font-size: 18px; }
    p { font-size: 13px; margin-bottom: 16px; }

    .examples {
      grid-template-columns: 1fr;
      gap: 8px;

      .example {
        padding: 10px 12px;
        font-size: 13px;
      }

      /* Only show the first 3 suggestions on small screens */
      .example:nth-child(n+4) { display: none; }
    }
  }
`;
